import { z } from 'zod';

// OAuth Status Schemas
export const OAuthTokenInfoSchema = z.object({
  hasAccessToken: z.boolean(),
  hasRefreshToken: z.boolean(),
  accessTokenPreview: z.string().nullable(),
  refreshTokenPreview: z.string().nullable(),
  expiresAt: z.string().nullable(),
  isExpired: z.boolean(),
  minutesToExpiry: z.number().nullable(),
  timeToExpiryMs: z.number().nullable(),
  clientId: z.string().nullable(),
  twitterUserId: z.string().nullable(),
  tokenCreatedAt: z.string().nullable(),
  tokenAgeMs: z.number().nullable(),
  tokenLifetimeMs: z.number().nullable(),
  tokenLifetimeHours: z.number().nullable(),
  tokenAgeHours: z.number().nullable(),
});

export const OAuthRateLimitInfoSchema = z.object({
  userInfo: z
    .object({
      id: z.string(),
      name: z.string().optional(),
      username: z.string().optional(),
    })
    .catchall(z.unknown())
    .optional(),
  timestamp: z.string().optional(),
});

export const OAuthTwitterApiTestSchema = z.object({
  success: z.boolean(),
  error: z.string().nullable(),
  responseTime: z.number().nullable(),
  rateLimitInfo: OAuthRateLimitInfoSchema.nullable(),
  testedAt: z.string(),
});

export const OAuthAccountInfoSchema = z.object({
  twitterProfileId: z.string(),
  accountCreated: z.string().nullable(),
  lastSocialSync: z.string().nullable(),
  tokensLastUpdated: z.string().nullable(),
});

export const OAuthSessionInfoSchema = z.object({
  sessionUserId: z.string(),
  twitterUsername: z.string().nullable(),
  sessionError: z.string().nullable(),
  hasDebugFeature: z.boolean(),
});

export const OAuthDebugInfoSchema = z.object({
  currentTime: z.string(),
  tokenExpiryCheck: z.object({
    expiresAt: z.string(),
    currentTime: z.string(),
    isExpired: z.boolean(),
    timeDiff: z.string(),
  }),
});

export const OAuthStatusResponseSchema = z.object({
  status: z.enum(['healthy', 'warning', 'expired', 'invalid']),
  statusMessage: z.string(),
  hasValidTokens: z.boolean(),
  tokenInfo: OAuthTokenInfoSchema,
  twitterApiTest: OAuthTwitterApiTestSchema,
  accountInfo: OAuthAccountInfoSchema,
  sessionInfo: OAuthSessionInfoSchema,
  debugInfo: OAuthDebugInfoSchema,
});

// Tenant Validation Schemas
export const TenantValidationResponseSchema = z.object({
  valid: z.boolean(),
  reason: z.string(),
});
export type TenantValidationResponse = z.infer<typeof TenantValidationResponseSchema>;

// Error Redirect Schemas (just returns redirect URL in body)
export const AuthErrorRedirectQuerySchema = z.object({
  error: z.string().optional(),
  error_description: z.string().optional(),
});
export type AuthErrorRedirectQuery = z.infer<typeof AuthErrorRedirectQuerySchema>;

export const AuthErrorRedirectResponseSchema = z.object({
  redirectUrl: z.string(),
});
export type AuthErrorRedirectResponse = z.infer<typeof AuthErrorRedirectResponseSchema>;

export type OAuthTokenInfo = z.infer<typeof OAuthTokenInfoSchema>;
export type OAuthRateLimitInfo = z.infer<typeof OAuthRateLimitInfoSchema>;
export type OAuthTwitterApiTest = z.infer<typeof OAuthTwitterApiTestSchema>;
export type OAuthAccountInfo = z.infer<typeof OAuthAccountInfoSchema>;
export type OAuthSessionInfo = z.infer<typeof OAuthSessionInfoSchema>;
export type OAuthDebugInfo = z.infer<typeof OAuthDebugInfoSchema>;
export type OAuthStatusResponse = z.infer<typeof OAuthStatusResponseSchema>;
